<template>
  <v-text-field
      :error-messages="error"
      :label="translation(translationList.house_amount_insured, 'Sumă asigurată')"
      v-model="amountInsuredString"
      name="amount_insured"
      :rules="amountInsuredValidationRules"
      @input="onInput"
      variant="outlined"
      :tile="true"
      suffix="EUR"
      type="number"
  >
    <template v-slot:append-inner>
      <v-tooltip location="top" open-delay="500">
        <template v-slot:activator="{ props }">
           <v-icon v-bind="props" class="cursor-pointer" aria-label="info">mdi-information</v-icon>
        </template>
        <span>
             Valoarea evaluata aproximativa a locuintei sau bunului, obligatorie doar pt asigurare facultativa (Minim 30.000 EUR)
        </span>
      </v-tooltip>
    </template>
  </v-text-field>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, toRefs } from 'vue';
import { useTranslationStore, translation } from '~/plugins/store/translations';
import type {TranslationList} from "~/types/Utils";

export default defineComponent({
  props: {
    modelValue: Number as () => number | null,
  },
  setup(props, { emit }) {
    const { modelValue } = toRefs(props);
    const amountInsured = ref<number | null>(modelValue.value ?? null);
    const amountInsuredString = ref<string | null>(modelValue.value !== null ? String(modelValue.value) : null);
    const error = ref<string[]>([]);

    watch(amountInsured, (newValue) => {
      const isValid = validateAmountInsured(newValue);
      if (isValid) {
        emit('update:modelValue', newValue);
      } else {
        emit('update:modelValue', null);
      }
      emit('validation-house-status', { field: 'amount_insured', isValid });
    });

    watch(amountInsuredString, (newValue) => {
      const parsedValue = parseFloat(newValue as string);
      if (!isNaN(parsedValue)) {
        amountInsured.value = parsedValue;
      } else {
        amountInsured.value = null;
      }
    });

    function validateAmountInsured(value: number | null, showErrors?: boolean): boolean {
      error.value = [];
      if (value !== null) {
        if (showErrors) {
          const errors = amountInsuredValidationRules
              .map(rule => rule(value))
              .filter(result => result !== true) as string[];
          if (errors.length > 0) {
            error.value = errors;
            return false;
          }
        }
        return amountInsuredValidationRules.every(rule => rule(value) === true);
      } else if (showErrors) {
        error.value.push(translation(translationList.value.required, 'Acest câmp este obligatoriu'));
        return false;
      }
      return false;
    }

    const translationList = ref<TranslationList>({});
    const translationStore = useTranslationStore();

    const amountInsuredValidationRules = [
      (v: number) => !!v || translation(translationList.value.required, 'Acest câmp este obligatoriu'),
      (v: number) => v >= 0 || translation(translationList.value.invalid_field, 'Acest câmp este invalid.'),
    ];

    const onInput = (event: Event) => {
      const input = event.target as HTMLInputElement;
      amountInsuredString.value = input.value;
    };

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');

      // Listen for validate event
      window.addEventListener('validate-house', (data) => {
        if (data.detail && data.detail === 'amount_insured') {
          const isValid = validateAmountInsured(amountInsured.value, true);
          if (isValid) {
            emit('validation-house-status', { field: 'amount_insured', isValid });
          }
        }
      });
    });

    watch(modelValue, (newValue) => {
      const formattedValue = newValue !== null ? String(newValue) : null;
      if (formattedValue !== amountInsuredString.value) {
        amountInsuredString.value = formattedValue;
      }
    });

    return {
      amountInsured,
      amountInsuredString,
      amountInsuredValidationRules,
      translationList,
      translation,
      onInput,
      error,
    };
  },
});
</script>